import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Library, 
  Plus, 
  Search, 
  Utensils, 
  User, 
  Phone, 
  Mail, 
  Calendar,
  ShieldCheck,
  Heart,
  Briefcase,
  Star
} from 'lucide-react';
import { CustomRSVPQuestion } from './CustomRSVPBuilder';
import { toast } from 'sonner';

interface QuestionTemplateLibraryProps {
  onAddQuestions: (questions: CustomRSVPQuestion[]) => void;
}

interface QuestionTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: React.ReactNode;
  questions: Omit<CustomRSVPQuestion, 'id'>[];
}

const QUESTION_TEMPLATES: QuestionTemplate[] = [
  {
    id: 'dietary',
    name: 'Dietary Restrictions',
    description: 'Collect dietary preferences and allergies',
    category: 'Dining',
    icon: <Utensils className="h-4 w-4" />,
    questions: [
      {
        text: 'Do you have any dietary restrictions?',
        type: 'select',
        options: ['None', 'Vegetarian', 'Vegan', 'Gluten-free', 'Dairy-free', 'Nut allergies', 'Other'],
        required: true,
        category: 'Dietary',
        placeholder: 'Select your dietary restriction'
      },
      {
        text: 'Please specify any food allergies',
        type: 'textarea',
        required: false,
        category: 'Dietary',
        placeholder: 'List any allergies or special dietary needs',
        helpText: 'Help us ensure your meal is safe and enjoyable'
      }
    ]
  },
  {
    id: 'contact',
    name: 'Contact Information',
    description: 'Basic contact details for guests',
    category: 'Contact',
    icon: <Mail className="h-4 w-4" />,
    questions: [
      {
        text: 'Full Name',
        type: 'text',
        required: true,
        category: 'Contact',
        placeholder: 'John Doe'
      },
      {
        text: 'Email Address',
        type: 'email',
        required: true,
        category: 'Contact',
        placeholder: 'john@example.com'
      },
      {
        text: 'Phone Number',
        type: 'phone',
        required: true,
        category: 'Contact',
        placeholder: '+1 (555) 123-4567'
      }
    ]
  },
  {
    id: 'plus-one',
    name: 'Plus One Details',
    description: 'Guest and additional attendee information',
    category: 'General',
    icon: <User className="h-4 w-4" />,
    questions: [
      {
        text: 'Will you be bringing a +1?',
        type: 'radio',
        options: ['Yes', 'No'],
        required: true,
        category: 'General'
      },
      {
        text: "Guest's Name",
        type: 'text',
        required: false,
        category: 'General',
        placeholder: 'Full name of your guest',
        helpText: 'Only required if bringing a +1'
      }
    ]
  },
  {
    id: 'accessibility',
    name: 'Accessibility Needs',
    description: 'Ensure venue accommodates all guests',
    category: 'Accessibility',
    icon: <ShieldCheck className="h-4 w-4" />,
    questions: [
      {
        text: 'Do you require any accessibility accommodations?',
        type: 'multiselect',
        options: [
          'Wheelchair accessible seating',
          'Sign language interpreter',
          'Hearing assistance',
          'Visual assistance',
          'Service animal accommodation',
          'Other'
        ],
        required: false,
        category: 'Accessibility'
      },
      {
        text: 'Please describe your accessibility needs',
        type: 'textarea',
        required: false,
        category: 'Accessibility',
        placeholder: 'Any additional details to help us accommodate you',
        helpText: 'We want to ensure everyone can fully participate'
      }
    ]
  },
  {
    id: 'emergency',
    name: 'Emergency Contact',
    description: 'Emergency contact information',
    category: 'Emergency',
    icon: <Phone className="h-4 w-4" />,
    questions: [
      {
        text: 'Emergency Contact Name',
        type: 'text',
        required: false,
        category: 'Emergency',
        placeholder: 'Full name'
      },
      {
        text: 'Emergency Contact Phone',
        type: 'phone',
        required: false,
        category: 'Emergency',
        placeholder: '+1 (555) 123-4567'
      },
      {
        text: 'Relationship to Emergency Contact',
        type: 'select',
        options: ['Spouse', 'Parent', 'Sibling', 'Friend', 'Other'],
        required: false,
        category: 'Emergency'
      }
    ]
  },
  {
    id: 'transportation',
    name: 'Transportation & Parking',
    description: 'Travel and parking preferences',
    category: 'Preferences',
    icon: <Calendar className="h-4 w-4" />,
    questions: [
      {
        text: 'Will you need parking?',
        type: 'radio',
        options: ['Yes', 'No', 'Using rideshare/taxi'],
        required: false,
        category: 'Preferences'
      },
      {
        text: 'Estimated arrival time',
        type: 'time',
        required: false,
        category: 'Preferences',
        helpText: 'Help us plan logistics'
      }
    ]
  },
  {
    id: 'accommodation',
    name: 'Hotel Accommodation',
    description: 'Overnight stay requirements',
    category: 'Preferences',
    icon: <Heart className="h-4 w-4" />,
    questions: [
      {
        text: 'Will you need hotel accommodation?',
        type: 'radio',
        options: ['Yes', 'No'],
        required: false,
        category: 'Preferences'
      },
      {
        text: 'Number of nights',
        type: 'number',
        required: false,
        category: 'Preferences',
        placeholder: 'How many nights?',
        validation: { min: 1, max: 7 }
      },
      {
        text: 'Room preference',
        type: 'select',
        options: ['Single', 'Double', 'Suite', 'Accessible'],
        required: false,
        category: 'Preferences'
      }
    ]
  },
  {
    id: 'professional',
    name: 'Professional Event Info',
    description: 'Business event specific questions',
    category: 'Professional',
    icon: <Briefcase className="h-4 w-4" />,
    questions: [
      {
        text: 'Company/Organization',
        type: 'text',
        required: false,
        category: 'Professional',
        placeholder: 'Your company name'
      },
      {
        text: 'Job Title',
        type: 'text',
        required: false,
        category: 'Professional',
        placeholder: 'Your role'
      },
      {
        text: 'T-shirt Size',
        type: 'select',
        options: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'],
        required: false,
        category: 'Professional'
      }
    ]
  },
  {
    id: 'preferences',
    name: 'Event Preferences',
    description: 'Activity and preference collection',
    category: 'Preferences',
    icon: <Star className="h-4 w-4" />,
    questions: [
      {
        text: 'What activities are you most interested in?',
        type: 'multiselect',
        options: [
          'Networking',
          'Workshops',
          'Entertainment',
          'Dining',
          'Outdoor activities',
          'Tours'
        ],
        required: false,
        category: 'Preferences'
      },
      {
        text: 'Any special requests or comments?',
        type: 'textarea',
        required: false,
        category: 'Preferences',
        placeholder: 'Let us know how we can make this event special for you',
        helpText: 'Optional but appreciated'
      }
    ]
  }
];

export function QuestionTemplateLibrary({ onAddQuestions }: QuestionTemplateLibraryProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());

  const categories = ['all', ...Array.from(new Set(QUESTION_TEMPLATES.map(t => t.category)))];

  const filteredTemplates = QUESTION_TEMPLATES.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'all' || template.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleToggleTemplate = (templateId: string) => {
    setSelectedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const handleApplyTemplates = () => {
    const templatesToAdd = QUESTION_TEMPLATES.filter(t => selectedTemplates.has(t.id));
    const allQuestions = templatesToAdd.flatMap(template => 
      template.questions.map((q, index) => ({
        ...q,
        id: `${template.id}_${Date.now()}_${index}`
      }))
    );
    
    onAddQuestions(allQuestions as CustomRSVPQuestion[]);
    toast.success(`Added ${allQuestions.length} questions from ${templatesToAdd.length} template(s)`);
    setSelectedTemplates(new Set());
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="default" size="lg" className="gap-2 shadow-md hover:shadow-lg transition-all">
          <Library className="h-5 w-5" />
          Browse Templates
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Library className="h-6 w-6 text-primary" />
            Professional Question Templates
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {selectedTemplates.size > 0 
              ? `${selectedTemplates.size} template(s) selected • Click templates to add or remove them`
              : 'Select one or more templates to build your RSVP form instantly'}
          </p>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-y-auto">
          <div className="flex gap-4 items-center sticky top-0 bg-background z-10 pb-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <Tabs value={activeCategory} onValueChange={setActiveCategory}>
            <TabsList className="w-full justify-start overflow-x-auto">
              {categories.map(cat => (
                <TabsTrigger key={cat} value={cat} className="capitalize">
                  {cat}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {filteredTemplates.map(template => {
                const isSelected = selectedTemplates.has(template.id);
                return (
                  <Card 
                    key={template.id} 
                    className={`hover:shadow-lg transition-all cursor-pointer border-2 ${
                      isSelected 
                        ? 'border-primary bg-primary/5 shadow-md' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => handleToggleTemplate(template.id)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2 flex-1">
                          <div className={`p-2 rounded-lg mt-1 transition-colors ${
                            isSelected 
                              ? 'bg-primary text-primary-foreground' 
                              : 'bg-primary/10 text-primary'
                          }`}>
                            {template.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-base flex items-center gap-2">
                              <span className="truncate">{template.name}</span>
                              {isSelected && (
                                <Badge variant="default" className="text-xs shrink-0">✓</Badge>
                              )}
                            </CardTitle>
                            <CardDescription className="text-xs mt-1">
                              {template.description}
                            </CardDescription>
                            <Badge variant="outline" className="text-xs mt-2">
                              {template.category}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-1">
                        <p className="text-xs font-semibold">
                          {template.questions.length} question{template.questions.length > 1 ? 's' : ''}:
                        </p>
                        <ul className="text-xs space-y-1.5">
                          {template.questions.slice(0, 2).map((q, index) => (
                            <li key={index} className="flex items-start gap-1.5">
                              <span className="text-primary font-bold mt-0.5">•</span>
                              <span className="text-muted-foreground flex-1">{q.text}</span>
                            </li>
                          ))}
                          {template.questions.length > 2 && (
                            <li className="text-muted-foreground italic text-xs pl-3">
                              +{template.questions.length - 2} more...
                            </li>
                          )}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {filteredTemplates.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Library className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No templates found matching your search.</p>
              </div>
            )}
          </Tabs>
        </div>

        <div className="flex items-center justify-between pt-4 border-t gap-4">
          <p className="text-sm text-muted-foreground">
            {selectedTemplates.size > 0 
              ? `${selectedTemplates.size} template(s) • ${QUESTION_TEMPLATES.filter(t => selectedTemplates.has(t.id)).reduce((sum, t) => sum + t.questions.length, 0)} total questions`
              : 'Click templates above to select them'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApplyTemplates}
              disabled={selectedTemplates.size === 0}
              size="lg"
            >
              Add {selectedTemplates.size > 0 ? `${selectedTemplates.size} Template${selectedTemplates.size > 1 ? 's' : ''}` : 'Templates'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}